import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseForm } from '../../../../base-form';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { build, pool } from '@fitogest/reactive-form-builder/utils';
import { FitoLocalesFormDefinition } from './form-control/form-definition';
import {
    DropdownData,
    FormType,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
    Value,
} from '@fitogest/reactive-form-builder/interfaces';
import type { FitoLocalesModel } from 'src/app/models/tareas/fito-locales.model';
import type { ProductosModel } from 'src/app/models/productos/productos.model';
import type { FincasModel } from 'src/app/models/form-common/fincas.module';

@Component({
    selector: 'app-fito-locales-form',
    standalone: false,
    templateUrl: './fito-locales-form.component.html',
    styleUrls: ['./fito-locales-form.component.scss'],
})
export class FitoLocalesFormComponent extends BaseForm<FitoLocalesModel> implements OnInit {
    public readonly reactiveFormLoadingLabel = 'Preparando formulario fitosanitario en locales...';

    public form!: FitoLocalesFormDefinition;
    public override model: FitoLocalesModel = {};
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public loadingData = true;
    public initialPreviousData: FitoLocalesModel | null = null;
    public reactiveFormRecordId = '';
    public getRequest = this.tareasApi.fitoLocales.GET;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public clientesApi: ClientesApiService,
        public fincasApi: FincasApiService,
        public productosApi: ProductosApiService,
        public tareasApi: TareasApiService,
        public override router: Router,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.fitoLocales.PUT,
            tareasApi.fitoLocales.POST,
            'fito-locales',
            'Editar Tarea Fitosanitaria en locales',
            'Crear Tarea Fitosanitaria en locales',
            'Duplicar Tarea Fitosanitaria en locales',
        );
    }

    public async ngOnInit(): Promise<void> {
        try {
            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
            const initialModel = this.initializeHostModel(this.toFitoLocalesModel(this.getModel() ?? {}));

            this.initialPreviousData = { ...initialModel };
            this.form = new FitoLocalesFormDefinition(this.getType(), initialModel);
            this.form.model = this.toFitoLocalesModel(initialModel);

            await this.initFormData();
            this.syncModelFromForm();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            console.error('Error loading fito locales form data.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<FitoLocalesModel>): void {
        const stableValue = this.captureStableValueChange(event.value as FitoLocalesModel);

        this.form.model = this.toFitoLocalesModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<FitoLocalesModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as FitoLocalesModel);

        this.form.model = this.toFitoLocalesModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
    }

    public afterSend(): void {
        if (this.getType() === FormType.DUPLICATION) {
            window.location.reload();
        }
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<FitoLocalesModel>,
        changedField: string | number | symbol | null | undefined,
    ): void {
        this.form.model = this.toFitoLocalesModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (String(changedField ?? '') === 'id_cliente') {
            this.form.model = this.toFitoLocalesModel({
                ...this.form.model,
                id_finca: '',
            });
            this.syncModelFromForm();
            this.formBuilderRef?.updateForm({ id_finca: '' }, true);
            this.refreshReactiveDropdownMappings();
        }
    }

    private async initFormData(): Promise<void> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        await pool(this.getDropdownBuildsForConfiguredCatalogs(), () => this.refreshReactiveDropdownMappings());

        this.form.productos.values = this.filterProductosFitosanitarios(this.form.productos);
        this.form.productos.filtered = this.filterProductosFitosanitarios(this.form.productos);
        this.applyCreationDefaults();
    }

    private getDropdownBuildsForConfiguredCatalogs(): ReturnType<typeof build>[] {
        const configuredCatalogs = this.getConfiguredDropdownCatalogs();
        const buildsRequest: ReturnType<typeof build>[] = [];

        if (configuredCatalogs.has('id_cliente')) {
            buildsRequest.push(build(this.clientesApi.clientesGET, this.form.clientes, 'nombre'));
        }

        if (configuredCatalogs.has('id_finca')) {
            buildsRequest.push(build(this.fincasApi.fincasGET, this.form.fincas, 'nombre', true));
        }

        if (configuredCatalogs.has('id_producto')) {
            buildsRequest.push(build(this.productosApi.productos.GET, this.form.productos, 'nombre'));
        }

        return buildsRequest;
    }

    private getConfiguredDropdownCatalogs(): Set<string> {
        const dropdownCatalogs = this.form.formFields.dropdownCatalogs;

        return new Set([
            ...(dropdownCatalogs?.critical ?? []),
            ...(dropdownCatalogs?.deferred ?? []),
            ...(dropdownCatalogs?.lazy ?? []),
        ]);
    }

    private markReactiveFormReady(): void {
        this.loadingData = false;
        this.isReactiveFormReady = true;
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toFitoLocalesModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toFitoLocalesModel(sourceModel: Partial<FitoLocalesModel> | ReactiveFormValue<FitoLocalesModel>): FitoLocalesModel {
        const normalizedModel: Record<string, unknown> = {
            ...(sourceModel as unknown as Record<string, unknown>),
        };

        return normalizedModel as unknown as FitoLocalesModel;
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim().length > 0) {
            return error.message;
        }

        if (typeof error === 'string' && error.trim().length > 0) {
            return error;
        }

        return 'No se ha podido preparar el formulario fitosanitario en locales.';
    }

    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private filterProductosFitosanitarios(productos: DropdownData<ProductosModel>): Value<ProductosModel>[] {
        const filtered: Value<ProductosModel>[] = [{ label: '...', value: null }];

        for (const producto of productos.values ?? []) {
            if (producto.value?.tipo === 'fitosanitario') {
                filtered.push(producto);
            }
        }

        return filtered;
    }

    private applyCreationDefaults(): void {
        if (this.getType() !== FormType.CREATION || this.form.fincas.values.length !== 2) {
            return;
        }

        const onlyFinca = this.form.fincas.values[1]?.value as FincasModel | null | undefined;

        if (!onlyFinca?.id) {
            return;
        }

        this.form.model = this.toFitoLocalesModel({
            ...this.form.model,
            id_finca: String(onlyFinca.id),
        });
    }
}
